import { FC, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useStewardshipStore } from '../../../store/useStewardshipStore';
import { NavigateTitle } from '../../navigate-title/NavigateTitle';
import { TableStewardshipLoading } from '../TableLoading';
import { TableEmployeesList } from './table/TableEmployeesList.tsx';
import { StatisticsCard } from '../../header/StatisticsCard';
import { useTranslation } from 'react-i18next';
import useStore from '@/lib/store';

export const StewardshipEmployeesList: FC = () => {
  const [searchParams] = useSearchParams();
  const plant_id = searchParams.get('plant_id');
  const organization_id = searchParams.get('organization_id') as string;
  const farmer_type = searchParams.get('farmer_type') as string;
  const { region_id, district_id } = useParams();
  const { t } = useTranslation();
  const { plants } = useStore();

  const {
    responsibleUsersCount,
    responsibleUsersCountLoading,
    responsibleEmployees,
    responsibleEmployeesLoading,
    fetchResponsibleUsersCount,
    fetchResponsibleEmployees,
  } = useStewardshipStore();

  useEffect(() => {
    if (district_id && plant_id) {
      fetchResponsibleUsersCount({
        plant_id,
        region_id,
        district_id,
        organization_id,
        farmer_type,
      });
      fetchResponsibleEmployees({ district_id, plant_id, organization_id, farmer_type });
    }
  }, [district_id, plant_id, region_id, organization_id, farmer_type]);

  // Get selected plant name
  const selectedPlant = useMemo(() => {
    return plants?.find((plant) => plant.id === plant_id);
  }, [plants, plant_id]);

  // Get district name from employees data if available
  const districtName = useMemo(() => {
    return responsibleEmployees.length > 0
      ? responsibleEmployees[0]?.district_name
      : undefined;
  }, [responsibleEmployees]);

  return (
    <div className="flex flex-col gap-4 p-8 h-full">
      <div className="flex items-center justify-between flex-shrink-0">
        <NavigateTitle
          regionId={region_id}
          districtId={district_id}
          districtName={districtName}
        />
        {responsibleUsersCount && selectedPlant && (
          <div className="flex gap-2">
            <StatisticsCard
              icon={null}
              label={t('responsible.users.count')}
              count={Number(responsibleUsersCount.responsible_users_count)}
              countColor="text-green-green-700"
            />
            <StatisticsCard
              icon={null}
              label={t('projects.count')}
              count={Number(responsibleUsersCount.projects_count)}
              countColor="text-dark-blue-300"
            />
          </div>
        )}
      </div>
      <div className="flex-1 overflow-hidden">
        {responsibleUsersCountLoading || responsibleEmployeesLoading ? (
          <TableStewardshipLoading />
        ) : (
          <TableEmployeesList employees={responsibleEmployees} />
        )}
      </div>
    </div>
  );
};
